import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "@/components/ui/Dialog";
import { ApiFieldErrors } from "@/components/ui/ApiFieldErrors";
import { apiFieldDescribedBy, apiFieldHasError } from "@/components/ui/api-field-errors";
import { useCreateApp } from "../hooks/useApps";

interface CreateAppDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateAppDialog({ open, onClose }: CreateAppDialogProps) {
  const { t } = useTranslation();
  const createApp = useCreateApp();
  const [name, setName] = useState("");
  const fieldErrorPrefix = useId();
  const nameInvalid = apiFieldHasError(createApp.error, "name");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (createApp.isPending) return;
    if (!name.trim()) return;

    createApp.mutate(
      { name: name.trim() },
      {
        onSuccess: () => {
          setName("");
          onClose();
        },
      },
    );
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (createApp.error) createApp.reset();
  };

  const handleClose = () => {
    if (!createApp.isPending) {
      setName("");
      createApp.reset();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t("apps.createApp")}
      description={t(
        "apps.createAppDescription",
        "Your app will be provisioned with Production and Staging deployments.",
      )}
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="app-name" className="text-sm font-medium text-subtle">
            {t("apps.appNameLabel")}
          </label>
          <input
            id="app-name"
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder={t("apps.appNamePlaceholder")}
            data-autofocus
            readOnly={createApp.isPending}
            aria-disabled={createApp.isPending || undefined}
            aria-invalid={nameInvalid ? true : undefined}
            aria-describedby={apiFieldDescribedBy(createApp.error, "name", fieldErrorPrefix)}
            className="
              h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading
              transition-colors duration-150
              placeholder:text-muted
              focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo
              aria-disabled:cursor-not-allowed aria-disabled:opacity-50
              hover:border-border-light
            "
          />
        </div>

        <ApiFieldErrors
          error={createApp.error}
          fieldLabels={{ name: t("apps.appNameLabel") }}
          idPrefix={fieldErrorPrefix}
          className="mt-3"
        />

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            aria-disabled={createApp.isPending || undefined}
            className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={!createApp.isPending && !name.trim()}
            aria-disabled={createApp.isPending || undefined}
            aria-busy={createApp.isPending || undefined}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {createApp.isPending ? t("apps.creating") : t("apps.createApp")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
